import { Box, Checkbox, MenuItem, Select, Switch, TextField, Tooltip, Typography } from '@mui/material'
import type { CheckDescriptor, ReferenceLayer, RunConfig } from '../../api/types'
import { EudrTag, Surface, TierPill } from '../../components/primitives'
import { tokens } from '../../theme'
import { byPanelOrder } from './checkMeta'
import type { ParamControl } from '../../api/types'

interface Props {
  checks: CheckDescriptor[]
  config: RunConfig
  layers: ReferenceLayer[]
  onChange: (next: RunConfig) => void
  header?: React.ReactNode
}

/**
 * The 1a checks panel: two tiers, a toggle per check, and each check's own
 * parameters or reference-layer select on the right.
 */
export function ChecksPanel({ checks, config, layers, onChange, header }: Props) {
  const setEnabled = (key: string, enabled: boolean) =>
    onChange({ ...config, checks: { ...config.checks, [key]: { ...config.checks[key]!, enabled } } })

  const setParam = (key: string, param: string, value: unknown) =>
    onChange({
      ...config,
      checks: {
        ...config.checks,
        [key]: { ...config.checks[key]!, params: { ...config.checks[key]!.params, [param]: value } },
      },
    })

  // The default layer of each kind, and only that: a run is checked against
  // whatever the platform has registered as the default (the API ignores
  // any other choice), so offering one here would be a choice that does
  // nothing.
  const layerFor = (kind: ReferenceLayer['kind']) =>
    layers.find((l) => l.kind === kind && l.is_default)?.id ?? ''

  const boundary = layers.find((l) => l.id === layerFor('country_boundary'))

  const renderControl = (check: CheckDescriptor, control: ParamControl) => {
    const entry = config.checks[check.key]
    switch (control.kind) {
      case 'number':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
            {control.label && (
              <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {control.label}
              </Typography>
            )}
            <TextField
              type="number"
              size="small"
              value={(entry?.params?.[control.key] as number) ?? ''}
              onChange={(e) => setParam(check.key, control.key, Number(e.target.value))}
              disabled={!entry?.enabled}
              inputProps={{
                step: control.step, min: control.min, max: control.max,
                'aria-label': `${check.ui?.panelLabel ?? check.label} ${control.label ?? control.key}`,
                style: { textAlign: 'left', fontVariantNumeric: 'tabular-nums' },
              }}
              InputProps={{
                endAdornment: control.unit ? (
                  <Typography variant="caption" sx={{ color: 'text.disabled', pl: 1 }}>{control.unit}</Typography>
                ) : undefined,
                sx: { height: 32, width: 96, '& input': { height: 30, fontSize: 13 } },
              }}
            />
          </Box>
        )
      case 'checkbox':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <Checkbox
              size="small"
              checked={Boolean(entry?.params?.[control.key])}
              onChange={(e) => setParam(check.key, control.key, e.target.checked)}
              disabled={!entry?.enabled}
              inputProps={{ 'aria-label': control.label }}
              sx={{ '&.Mui-checked': { color: tokens.color.brandDeep } }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{control.label}</Typography>
          </Box>
        )
      case 'layer': {
        const layer = layers.find((l) => l.id === layerFor(control.layerKind))
        const label = layer ? `${layer.name.replace(/ \(.*\)$/, '')} ${layer.version}` : 'No layer registered'
        return (
          // The default layer is the only one a run can use — the API ignores
          // any other — so the list is fixed on it. Agribot's own layer is
          // shown to say it exists, as a premium option that cannot be picked.
          // Not MUI's `disabled`, which turns off pointer events and with them
          // the tooltip saying why.
          <Select
            size="small"
            value={layer?.id ?? ''}
            displayEmpty
            onChange={() => {}}
            disabled={!entry?.enabled}
            inputProps={{ 'aria-label': `${check.ui?.panelLabel ?? check.label} reference layer` }}
            renderValue={() => label}
            sx={{ height: 32, width: '100%', fontSize: 13, '& .MuiSelect-select': { py: 0 } }}
          >
            {layer && <MenuItem value={layer.id} sx={{ fontSize: 13 }}>{label}</MenuItem>}
            <MenuItem
              value="agribot"
              aria-disabled="true"
              disableRipple
              sx={{ fontSize: 13, color: 'text.disabled', cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
            >
              <Tooltip
                title="Premium layer: Agribot's own reference data. Contact Agribot to enable it."
                placement="left"
              >
                <Box component="span" sx={{ display: 'block', width: '100%' }}>Agribot</Box>
              </Tooltip>
            </MenuItem>
          </Select>
        )
      }
      case 'static':
        return (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary', textAlign: 'right', display: 'block',
              fontFamily: control.text.startsWith('EPSG') ? tokens.font.mono : undefined,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {control.text}
          </Typography>
        )
      default:
        return null
    }
  }

  const section = (tier: 'compliance' | 'data_quality', note: string) => {
    const rows = checks.filter((c) => c.tier === tier).sort(byPanelOrder)
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <TierPill tier={tier} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{note}</Typography>
        </Box>
        {/* Stretch, not centre: a 20px switch and a two-line label have to draw
            their hairline at the same height. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '36px minmax(0,1fr) 220px', alignItems: 'stretch', columnGap: 3 }}>
          {rows.map((check, i) => {
            const ui = check.ui ?? { control: { kind: 'none' as const } }
            const last = i === rows.length - 1
            const cell = {
              py: 3,
              borderBottom: last ? 0 : `1px solid ${tokens.color.ink200}`,
              display: 'flex',
              alignItems: 'center',
            }
            const entry = config.checks[check.key]
            return (
              <Box key={check.key} sx={{ display: 'contents' }}>
                <Box sx={cell}>
                  <Switch
                    checked={entry?.enabled ?? true}
                    onChange={(e) => setEnabled(check.key, e.target.checked)}
                    inputProps={{ 'aria-label': `${check.ui?.panelLabel ?? check.label} enabled` }}
                  />
                </Box>
                <Box sx={{ ...cell, minWidth: 0, flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ font: `500 14px ${tokens.font.sans}`, color: entry?.enabled ? 'text.primary' : 'text.disabled' }}>
                      {check.ui?.panelLabel ?? check.label}
                    </Typography>
                    {check.eudr && <EudrTag />}
                  </Box>
                  {(ui.subtitle || check.key === 'within_country') && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                      {check.key === 'within_country'
                        ? `Boundary layer: ${boundary ? `${boundary.name.replace(/ \(.*\)$/, '')} ${boundary.version}, admin level 0` : 'not configured'}`
                        : ui.subtitle}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ ...cell, justifyContent: 'flex-end' }}>
                  <Box sx={{ width: '100%' }}>{renderControl(check, ui.control)}</Box>
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    )
  }

  return (
    <Surface sx={{ p: 5, display: 'flex', flexDirection: 'column', gap: 4.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="h4">Checks</Typography>
        {header}
      </Box>
      {section('compliance', 'Failing polygons are rejected and cannot enter a region.')}
      {section('data_quality', "Flagged for review. Still usable.")}
    </Surface>
  )
}
